import transformSize from './transform-size'
import Logger from '../appl/logger'

type GoogleAdSlot = googletag.Slot

function initGoogleTag() {
    if (window.googletag) return
    window.googletag = { cmd: [] as googletag.CommandArray } as googletag.Googletag
}

function doCommand(command: Command) {
    window.googletag.cmd.push(command)
}

export default class GoogleAdService implements AdService {
    private readonly googleTagSlots: Dict<GoogleAdSlot> = {}

    constructor(config: HeaderBiddingConfig) {
        initGoogleTag()
        doCommand(() => this.setupGoogleTag(config))
    }

    /**
     * @override
     */
    public deliverAds(slots: SlotDefinition[]) {
        if (slots.length < 1) {
            Logger.debug('配信対象の広告枠がありません')
            return
        }
        doCommand(() => {
            const googleSlots = slots
                .filter(slot => slot.id in this.googleTagSlots)
                .map(slot => this.googleTagSlots[slot.id])
            this.refresh(googleSlots)
            Logger.debug('広告枠への広告配信を行いました', slots)
        })
    }

    public getSlots() {
        return Object.values(this.googleTagSlots)
    }

    public getSlotById(id: string) {
        return this.googleTagSlots[id]
    }

    private setupGoogleTag(config: HeaderBiddingConfig) {
        this.setTargeting(window.googletag.pubads(), config.targeting)
        window.googletag.pubads().disableInitialLoad()
        window.googletag.pubads().enableSingleRequest()
        window.googletag.pubads().collapseEmptyDivs()
        this.defineSlots(config.slots)
        window.googletag.enableServices()
    }

    private defineSlots(slots: SlotDefinition[]) {
        slots.forEach(slot => this.defineSlot(slot))
    }

    /**
     * 広告枠を追加する。
     */
    private defineSlot(slot: SlotDefinition) {
        Logger.debug(`広告枠定義 [${slot.name}]`, slot)
        const googleSlot = window.googletag
            .defineSlot(slot.name, transformSize(slot.size), slot.id)
            .addService(window.googletag.pubads())
        this.setTargeting(googleSlot, slot.targeting)
        this.googleTagSlots[slot.id] = googleSlot
    }

    private refresh(googleSlots: GoogleAdSlot[]) {
        googleSlots.forEach(googleSlot => this.display(googleSlot))
        window.googletag.pubads().refresh(googleSlots)
    }

    /**
     * 広告を表示する。display を実行するとインプレッションが発生する。
     */
    private display(slot: GoogleAdSlot) {
        window.googletag.display(slot.getSlotElementId())
    }

    /**
     * 表示ページまたは広告枠における Key-Value を設定する。
     * @param targeting Key-Value の連想配列
     */
    private setTargeting(target: googletag.PubAdsService | googletag.Slot, targeting?: KeyValue) {
        if (!targeting) return
        for (const key in targeting) {
            Logger.debug(`広告ターゲッティングを追加 [${key}]`, targeting[key])
            target.setTargeting(key, targeting[key])
        }
    }
}
